import { useCallback, type MouseEvent } from 'react'
import type { CalendarEvent } from '@highlight/common'
import { useAwaitMeetingJoin } from '@renderer/hooks/useAwaitMeetingJoin'
import { useAwaitingJoinMeetingId } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useIncognitoMode } from '@renderer/hooks/useIncognitoMode'
import { useMeetingMode } from '@renderer/hooks/settings/useMeetingMode'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { getIsGoogleMeet, getMeetingJoinLink } from '@shared/utils/connections/calendars'
import { joinDecidesTranscription } from '@shared/utils/meetings'
import { useOutOfMeetingHours } from './use-out-of-meeting-hours'

export interface JoinMeeting {
  /** Conference URI from the calendar event; undefined means there is nothing to join. */
  joinLink: string | undefined
  /**
   * "Join & Transcribe" in notify, where the click is what starts the recording.
   * "Join Meeting" in autopilot (it records either way, so naming the transcript
   * reads as consent the user already gave in Settings), in silent (nothing will
   * record), incognito (the armed join refuses to record), and out of hours (the
   * billing gate refuses the recording).
   */
  joinLabel: string
  /** A join for this meeting is in flight, waiting on meeting detection. */
  isAwaitingJoin: boolean
  /**
   * Stops propagation (the row behind the button also navigates) and joins.
   * @param triggerOverride replaces the hook's trigger on the tracked event when
   * one control (the hover cluster) needs telling apart from the host surface.
   */
  handleJoinClick: (e: MouseEvent, triggerOverride?: string) => void
}

/**
 * Join flow shared by the Home meeting cell and the Meeting View: arms
 * meeting-detection to auto-start recording (main saves any note running for
 * another meeting first), then opens the conference link with `authuser`
 * pinned to the event's account for Google Meet.
 */
export function useJoinMeeting(meeting: CalendarEvent | undefined, trackingTrigger: string): JoinMeeting {
  const awaitingJoinMeetingId = useAwaitingJoinMeetingId()
  const awaitMeetingJoin = useAwaitMeetingJoin()
  const { meetingMode } = useMeetingMode()
  const outOfHours = useOutOfMeetingHours()
  const { isIncognitoEnabled } = useIncognitoMode()

  const joinLink = meeting ? getMeetingJoinLink(meeting) : undefined

  const handleJoinClick = useCallback(
    (e: MouseEvent, triggerOverride?: string) => {
      e.stopPropagation()
      if (!meeting || !joinLink) return

      const joinMeeting = async () => {
        // Tracked before arming detection: awaiting the join first would tie
        // the event's delivery to an IPC round-trip it doesn't depend on.
        AmplitudeRenderer.trackEvent('Audio Note Action', {
          action: 'join scheduled meeting',
          trigger: triggerOverride ?? trackingTrigger,
        })
        // Armed before the link opens: arming first saves any note running for
        // another meeting, which must finish before this call can be detected.
        await awaitMeetingJoin({ source: 'base', meeting })
        try {
          const url = new URL(joinLink)
          if (meeting.userEmail && getIsGoogleMeet(meeting)) {
            url.searchParams.set('authuser', meeting.userEmail)
          }
          window.open(url.toString(), '_blank', 'noopener,noreferrer')
        } catch {
          window.open(joinLink, '_blank', 'noopener,noreferrer')
        }
      }
      void joinMeeting()
    },
    [meeting, joinLink, awaitMeetingJoin, trackingTrigger],
  )

  return {
    joinLink,
    joinLabel: joinDecidesTranscription({ mode: meetingMode, incognito: isIncognitoEnabled, outOfHours })
      ? 'Join & Transcribe'
      : 'Join Meeting',
    isAwaitingJoin: !!meeting && awaitingJoinMeetingId === meeting.id,
    handleJoinClick,
  }
}
